import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { getProducts, getCollections } from '../lib/shopify';
import ProductCard from '../components/product/ProductCard';
import { Filter, X } from 'lucide-react';

const JewelleryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  
  const currentCollection = searchParams.get('collection');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [productsData, collectionsData] = await Promise.all([
          getProducts(currentCollection),
          getCollections()
        ]);
        setProducts(productsData.products || []);
        setCollections(collectionsData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Unable to load products. Please try again later.');
        // Set empty arrays on error - the UI will show error message
        setProducts([]);
        setCollections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentCollection]);

  const handleCollectionChange = (handle) => {
    if (handle) {
      setSearchParams({ collection: handle });
    } else {
      setSearchParams({});
    }
    setFilterOpen(false);
  };

  // Filter collections to show only Bracelets and Earrings categories
  const mainCategories = ['bracelets', 'earrings', 'bracelet', 'earring'];
  const filteredCollections = collections.filter(c => 
    mainCategories.some(cat => c.handle?.toLowerCase().includes(cat) || c.title?.toLowerCase().includes(cat))
  );

  return (
    <>
      <Helmet>
        <title>Shop Jewellery - Bracelets & Earrings | LuxuryStyle</title>
        <meta 
          name="description" 
          content="Shop our collection of waterproof, tarnish-free bracelets and earrings. 18K gold plated and stainless steel jewellery designed for everyday luxury." 
        />
        <meta property="og:title" content="Shop Jewellery - LuxuryStyle" />
        <meta property="og:description" content="Premium waterproof bracelets and earrings collection." />
        <link rel="canonical" href="https://luxurystyle.info/jewellery" />
      </Helmet>

      <main data-testid="jewellery-page" className="pt-20">
        {/* Page Header */}
        <section className="py-16 md:py-24 bg-[#0a0a0a] border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
              Our Collection
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mt-4 mb-4">
              {currentCollection ? currentCollection.charAt(0).toUpperCase() + currentCollection.slice(1) : 'All Jewellery'}
            </h1>
            <div className="gold-divider mb-6" />
            <p className="text-white/60 max-w-2xl mx-auto">
              Explore our curated collection of premium waterproof and tarnish-free jewellery.
              Each piece is crafted with care for lasting beauty.
            </p>
          </div>
        </section>

        {/* Filter Bar */}
        <section className="py-6 bg-[#050505] border-b border-white/5 sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between">
              {/* Desktop Categories */}
              <div className="hidden md:flex items-center space-x-6">
                <button
                  onClick={() => handleCollectionChange(null)}
                  data-testid="filter-all"
                  className={`font-accent text-xs uppercase tracking-[0.2em] transition-colors ${
                    !currentCollection ? 'text-[#D4AF37]' : 'text-white/60 hover:text-white'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => handleCollectionChange('bracelets')}
                  data-testid="filter-bracelets"
                  className={`font-accent text-xs uppercase tracking-[0.2em] transition-colors ${
                    currentCollection === 'bracelets' ? 'text-[#D4AF37]' : 'text-white/60 hover:text-white'
                  }`}
                >
                  Bracelets
                </button>
                <button
                  onClick={() => handleCollectionChange('earrings')}
                  data-testid="filter-earrings"
                  className={`font-accent text-xs uppercase tracking-[0.2em] transition-colors ${
                    currentCollection === 'earrings' ? 'text-[#D4AF37]' : 'text-white/60 hover:text-white'
                  }`}
                >
                  Earrings
                </button>
              </div>

              {/* Mobile Filter Button */}
              <button
                onClick={() => setFilterOpen(true)}
                data-testid="mobile-filter-button"
                className="md:hidden flex items-center space-x-2 text-white/60 hover:text-white transition-colors"
              >
                <Filter className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-accent text-xs uppercase tracking-[0.2em]">Filter</span>
              </button>

              {/* Product Count */}
              <p className="text-white/40 text-sm">
                {products.length} {products.length === 1 ? 'Product' : 'Products'}
              </p>
            </div>
          </div>
        </section>

        {/* Mobile Filter Drawer */}
        {filterOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black/80 z-50 md:hidden"
              onClick={() => setFilterOpen(false)}
            />
            <div className="fixed inset-y-0 left-0 w-80 bg-[#0a0a0a] z-50 md:hidden">
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-white font-accent text-sm uppercase tracking-[0.2em]">
                  Filter
                </h3>
                <button
                  onClick={() => setFilterOpen(false)}
                  className="text-white/60 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <button
                  onClick={() => handleCollectionChange(null)}
                  className={`block w-full text-left py-3 border-b border-white/5 font-accent text-sm uppercase tracking-[0.2em] ${
                    !currentCollection ? 'text-[#D4AF37]' : 'text-white/60'
                  }`}
                >
                  All Jewellery
                </button>
                <button
                  onClick={() => handleCollectionChange('bracelets')}
                  className={`block w-full text-left py-3 border-b border-white/5 font-accent text-sm uppercase tracking-[0.2em] ${
                    currentCollection === 'bracelets' ? 'text-[#D4AF37]' : 'text-white/60'
                  }`}
                >
                  Bracelets
                </button>
                <button
                  onClick={() => handleCollectionChange('earrings')}
                  className={`block w-full text-left py-3 border-b border-white/5 font-accent text-sm uppercase tracking-[0.2em] ${
                    currentCollection === 'earrings' ? 'text-[#D4AF37]' : 'text-white/60'
                  }`}
                >
                  Earrings
                </button>
              </div>
            </div>
          </>
        )}

        {/* Product Grid */}
        <section className="py-12 md:py-20 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-square bg-[#111] mb-4" />
                    <div className="h-4 bg-[#111] mb-2 w-3/4" />
                    <div className="h-4 bg-[#111] w-1/4" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-[#D4AF37] text-lg mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-luxury-outline"
                >
                  Retry
                </button>
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-white/60 text-lg mb-4">No products found in this collection.</p>
                <button
                  onClick={() => handleCollectionChange(null)}
                  className="btn-luxury-outline"
                >
                  View All Products
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default JewelleryPage;
